import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook to handle session timeout on inactivity.
 *
 * Uses REACT_APP_SESSION_TIMEOUT env var (in milliseconds) if available.
 * Defaults to 15 minutes (900000 ms) if not set.
 *
 * Args:
 *   timeoutMinutes: Number of minutes of inactivity before timeout (optional, overrides env var)
 *   onTimeout: Callback function when timeout occurs
 *
 * Returns:
 *   Object with { isActive, resetTimeout }
 */
export const useSessionTimeout = (timeoutMinutes, onTimeout) => {
  // Get timeout from env var (in milliseconds) or use default 15 minutes
  const sessionTimeoutMs = process.env.REACT_APP_SESSION_TIMEOUT || 900000;
  const timeoutMinutesFromEnv = sessionTimeoutMs / (60 * 1000);

  // Use provided timeoutMinutes if given, otherwise use env var value
  // eslint-disable-next-line no-unused-vars
  const finalTimeoutMinutes = timeoutMinutes !== undefined ? timeoutMinutes : timeoutMinutesFromEnv;
  const timeoutRef = useRef(null);
  const isActiveRef = useRef(true);

  const resetTimeout = useCallback(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      isActiveRef.current = false;
      if (onTimeout) {
        onTimeout();
      }
    }, finalTimeoutMinutes * 60 * 1000);
  }, [finalTimeoutMinutes, onTimeout]);

  useEffect(() => {
    // Activity event listeners
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      if (isActiveRef.current) {
        resetTimeout();
      }
    };

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Initialize timeout
    resetTimeout();

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [resetTimeout]);

  return {
    isActive: isActiveRef.current,
    resetTimeout
  };
};

export default useSessionTimeout;

